import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CreateSession from './pages/CreateSession';
import HostDashboard from './pages/HostDashboard';
import JoinSession from './pages/JoinSession';
import Listen from './pages/Listen';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuthStore } from './store/authStore';
import './App.css';

/**
 * Root route component that redirects based on auth state
 * - If authenticated: redirect to dashboard (or preserve redirect from location state)
 * - If not authenticated: show login page
 * - Shows nothing while auth is initializing to prevent flash
 */
function RootRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const location = useLocation();

  // Don't render anything until auth is initialized
  if (!isInitialized) {
    return null;
  }

  if (isAuthenticated) {
    // Check if there's a redirect destination in location state
    const redirectTo = (location.state as { from?: string })?.from;
    if (redirectTo) {
      // Preserve the redirect destination
      return <Navigate to={redirectTo} replace />;
    }
    // Default to dashboard if no redirect specified
    return <Navigate to="/dashboard" replace />;
  }

  return <Login />;
}

function AppContent() {
  const navigate = useNavigate();
  const { initializeAuth, clearAuth } = useAuthStore();

  // Initialize auth state on app load
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Handle token expiration
  useEffect(() => {
    const handleTokenExpiration = () => {
      // Clear auth state
      clearAuth();

      // Redirect to login
      navigate('/', { replace: true });

      // Show user-friendly message
      alert('Your session has expired. Please log in again.');
    };

    window.addEventListener('auth:token-expired', handleTokenExpiration);

    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpiration);
    };
  }, [clearAuth, navigate]);

  return (
    <Routes>
      {/* Root route - redirects based on auth state */}
      <Route path="/" element={<RootRoute />} />

      {/* Main dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Host routes */}
      <Route
        path="/create-session"
        element={
          <ProtectedRoute>
            <CreateSession />
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/:sessionId"
        element={
          <ProtectedRoute>
            <HostDashboard />
          </ProtectedRoute>
        }
      />

      {/* Listener routes */}
      <Route
        path="/join"
        element={
          <ProtectedRoute>
            <JoinSession />
          </ProtectedRoute>
        }
      />
      <Route
        path="/listen/:sessionId"
        element={
          <ProtectedRoute>
            <Listen />
          </ProtectedRoute>
        }
      />

      {/* Fallback - redirect to root which handles auth logic */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
